/**
 * SAB-1219: one franchise's REG schedule for a season, the season's last REG week, and every
 * franchise alias -- what the player card's full-season game log needs besides the stats.
 *
 * The three filters: season_year + season_type 'REG' (+ the franchise on either side). Week
 * is the merge key downstream. The last week is read, not hard-coded (2026 REG is 1-18).
 *
 * A failed read THROWS, so the caller gets no schedule rather than an empty one: an empty
 * schedule would turn every week without stats into a BYE.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { GameLogGame } from '@/lib/nflGameLog';

export interface NflFranchiseSchedule {
  games: GameLogGame[];
  lastWeek: number | null;
  aliasById: Record<string, string>;
}

export function useNflFranchiseSchedule(
  franchiseId: string | null | undefined,
  seasonYear: number | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ['nfl-franchise-schedule', seasonYear, franchiseId],
    enabled: enabled && !!franchiseId && !!seasonYear,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<NflFranchiseSchedule> => {
      // `as any`: chained .eq() on nfl_games tips the generated types into TS2589, the same
      // cast every nfl_games read carries. Rows are typed on the way out.
      const [games, last, franchises] = await Promise.all([
        (supabase as any)
          .from('nfl_games')
          .select('id, week, home_franchise_id, away_franchise_id, scheduled, status')
          .eq('season_year', seasonYear)
          .eq('season_type', 'REG')
          .or(`home_franchise_id.eq.${franchiseId},away_franchise_id.eq.${franchiseId}`),
        (supabase as any)
          .from('nfl_games')
          .select('week')
          .eq('season_year', seasonYear)
          .eq('season_type', 'REG')
          .order('week', { ascending: false })
          .limit(1),
        supabase.from('nfl_franchises').select('id, alias'),
      ]);
      if (games.error) throw games.error;
      if (last.error) throw last.error;
      if (franchises.error) throw franchises.error;

      const aliasById: Record<string, string> = {};
      for (const f of (franchises.data ?? []) as { id: string; alias: string | null }[]) aliasById[f.id] = f.alias || '';
      const lastRow = (last.data ?? [])[0] as { week: number } | undefined;
      return {
        games: (games.data ?? []) as GameLogGame[],
        lastWeek: lastRow ? Number(lastRow.week) : null,
        aliasById,
      };
    },
  });
}
